import React, { useEffect } from 'react';
import { BUTTON_THEMES, Form, InputField, LoaderComponent } from 'ht-styleguide';
import { SubmitHandler, useForm } from 'react-hook-form';

/* Store */
import { useHomeHubStoreUserFlowAttributes } from '@scenes/HomeHub/store/client/homehub.store';

/* Queries / Mutations */
import useUserMutationLogin from '@store/queries/user/useUserMutation.login';
import useUserMutationResetPassword from '@store/queries/user/useUserMutation.resetPassword';

/* Hooks */
import useRouteController from '@scenes/HomeHub/hooks/hook.routeController';
import { useUserIdSelector } from '@store/queries/user/useUser.selector';

/* Components */
import UserFlowHeader from '@scenes/HomeHub/pages/User/components/Header';
import { GridLayoutForm } from '@scenes/HomeHub/pages/User/components/GridLayouts';
import ButtonWrapper from '@scenes/HomeHub/pages/User/components/ButtonWrapper';
import UserFlowHeaderIcon from '@scenes/HomeHub/pages/User/components/ImageIcon';
import SubduedText from '@scenes/HomeHub/pages/User/components/SubduedText';
import PlainButton from '@components/UI/PlainButton';
import Either from '@components/Either';

/* Integrations */
import { TRACKING_TYPES } from '@integrations/segment/homehub';

/* Constants / Utils */
import { YupSchemaResolver } from '@constants/validations';
import { setErrorsHookFormErrors } from '@utils/errors';

import { TUserPageProps } from '@scenes/HomeHub/pages/User/index';

import styles from './user.module.scss';

type TYupProps = { password: string };

const UserSetPasswordStep = ({ userSegmentTracking }: TUserPageProps) => {
  /* Store */
  const { userEmail } = useHomeHubStoreUserFlowAttributes();

  /* Hooks */
  const { surveyRoute } = useRouteController();
  const userID = useUserIdSelector();

  /* Mutations & Queries */
  const { data, mutate, isPending, isSuccess } = useUserMutationLogin();
  const { mutate: resetPassword } = useUserMutationResetPassword();

  /* Hooks */
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isDirty },
  } = useForm<TYupProps>({ ...YupSchemaResolver(['password']) });

  /* ---------------- Methods ----------------- */
  const registerFormAttributes: SubmitHandler<TYupProps> = attributes => {
    mutate(
      { email: userEmail, ...attributes },
      {
        onSuccess: (d): any => {
          if (d.id) {
            surveyRoute();
            userSegmentTracking(TRACKING_TYPES.finishedSurvey);
          }
        },
      },
    );
  };

  const handleResendEmailLink = () => {
    resetPassword(userEmail);
  };

  /* Resaturate Errors */
  useEffect(() => {
    if (data?.error) {
      setErrorsHookFormErrors<TYupProps>({ setError, mutationData: data, field: 'password' });
    }
  }, [data?.error, data, setError]);

  return (
    <GridLayoutForm>
      <Form.Row classes="marginAuto width100">
        <Form.Column sm={4} md={6} mdOffset={1} lg={4} lgOffset={4}>
          <UserFlowHeaderIcon width="104" height="104" imageName="illus-message-sent" />
          <UserFlowHeader headerText={`A message was sent to ${userEmail}`} />
          <SubduedText classes="p1 paddingTop-tiny1">
            Check your inbox for a new message and click the link to set your new password. Return to this page after your password has been created.
          </SubduedText>
        </Form.Column>
      </Form.Row>
      <Form.Row classes="marginAuto width100">
        <Form.Column sm={4} md={6} mdOffset={1} lg={4} lgOffset={4}>
          <Either condition={isPending || data?.id || (isSuccess && !data.error) || userID}>
            <div className="paddinTop-small">
              <LoaderComponent />
            </div>

            <div className={styles.actionContainer}>
              <InputField
                placeholder="Enter your password"
                containerClass="paddingTop-small"
                type="password"
                label=""
                error={errors.password?.message}
                {...register('password')}
              />
              <ButtonWrapper
                className="paddingTop-small1"
                disabled={!isDirty}
                onClick={handleSubmit(registerFormAttributes)}
                theme={BUTTON_THEMES.PRIMARY}
              >
                Continue the Survey
              </ButtonWrapper>
              <div className="paddingTop-small2 text-align-center">
                <PlainButton onClickHandler={handleResendEmailLink}>Resend Email Link</PlainButton>
              </div>
            </div>
          </Either>
        </Form.Column>
      </Form.Row>
    </GridLayoutForm>
  );
};

export default React.memo(UserSetPasswordStep);
